import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { UserMinus } from 'lucide-react'
import { toast } from 'sonner'
import {
  deleteOrganisationUser,
  organisationUsersQueryOptions,
  updateOrganisationUser,
} from '@/api/resources/organisation-users'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { UserForm } from '@/components/users/user-form'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/users/$organisationId/system-managers/$userId/edit')({
  head: () => ({ meta: [{ title: 'Edit System Manager - MyJunea' }] }),
  component: EditUserRoute,
})

function EditUserRoute(): React.ReactElement {
  const { organisationId, userId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/users/$organisationId/system-managers',
        params: { organisationId },
        resetScroll: false,
      }),
  )
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const organisationIdNum = Number(organisationId)
  const userIdNum = Number(userId)

  const { data: users } = useQuery(organisationUsersQueryOptions(organisationIdNum))
  const user = users?.find((u) => u.id === userIdNum)

  const updateMutation = useMutation({
    mutationFn: async (values: Parameters<typeof updateOrganisationUser>[2]) =>
      updateOrganisationUser(organisationIdNum, userIdNum, values),
    onSuccess: () => {
      clearServerErrors()
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationUsers.all(organisationIdNum),
      })
      // Wallets shows the same name.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.walletUsers.all(organisationIdNum),
      })
      toast.success('System manager updated')
      handleClose()
    },
    onError: handleMutationError,
  })

  const removeMutation = useMutation({
    mutationFn: async () => deleteOrganisationUser(organisationIdNum, userIdNum),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationUsers.all(organisationIdNum),
      })
      toast.success('Removed as system manager')
      void navigate({
        to: '/users/$organisationId/system-managers',
        params: { organisationId },
        resetScroll: false,
      })
    },
    onError: handleMutationError,
  })

  function handleRemove(): void {
    removeMutation.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-lg">
        {user === undefined ? (
          <>
            <SheetHeader>
              <SheetTitle className="text-2xl font-extrabold tracking-tight">
                Edit system manager
              </SheetTitle>
            </SheetHeader>
            <LoadingSpinner />
          </>
        ) : (
          <>
            <UserForm
              key={user.id}
              user={user}
              title="Edit system manager"
              onSubmit={({ email: _email, ...rest }) => updateMutation.mutate(rest)}
              isPending={updateMutation.isPending}
              serverErrors={serverErrors}
            />
            <div className="px-4 pb-6">
              <Separator className="mb-6" />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="lg" className="w-full rounded-full">
                    <UserMinus className="size-4" />
                    Remove as system manager
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove as system manager</AlertDialogTitle>
                    <AlertDialogDescription>
                      This takes away their access to MyJunea and their role as contact person. They
                      keep their Junea Wallet access to this organisation, so you still find them on
                      Wallets.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      onClick={handleRemove}
                      disabled={removeMutation.isPending}
                    >
                      {removeMutation.isPending ? 'Removing...' : 'Remove'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
